import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const AuthContext = createContext();

// 30 minutes of inactivity before auto-logout
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
// Re-check JWT expiry every minute
const JWT_CHECK_INTERVAL_MS = 60 * 1000;

const decodeJwtExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = not authenticated
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);
  const jwtTimer = useRef(null);

  const clearTimers = () => {
    if (inactivityTimer.current) { clearTimeout(inactivityTimer.current); inactivityTimer.current = null; }
    if (jwtTimer.current) { clearInterval(jwtTimer.current); jwtTimer.current = null; }
  };

  const logout = useCallback((reason) => {
    clearTimers();
    localStorage.removeItem('elite_admin_token');
    setUser(null);
    if (reason === 'timeout' && window.location.pathname.startsWith('/admin')) {
      toast.message('Session expired', { description: 'You were logged out due to inactivity.' });
      if (!window.location.pathname.endsWith('/login')) {
        window.location.href = '/admin/login';
      }
    } else if (reason === 'jwt_expired' && window.location.pathname.startsWith('/admin')) {
      toast.message('Session expired', { description: 'Your session has expired, please sign in again.' });
      if (!window.location.pathname.endsWith('/login')) {
        window.location.href = '/admin/login';
      }
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => logout('timeout'), INACTIVITY_TIMEOUT_MS);
  }, [logout]);

  // Watch user activity & JWT expiry once authenticated.
  useEffect(() => {
    if (!user) { clearTimers(); return; }

    resetInactivityTimer();
    const evts = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    const onAct = () => resetInactivityTimer();
    evts.forEach((e) => window.addEventListener(e, onAct, { passive: true }));

    jwtTimer.current = setInterval(() => {
      const t = localStorage.getItem('elite_admin_token');
      if (!t) { logout('jwt_expired'); return; }
      const exp = decodeJwtExp(t);
      if (exp && exp < Date.now()) logout('jwt_expired');
    }, JWT_CHECK_INTERVAL_MS);

    return () => {
      evts.forEach((e) => window.removeEventListener(e, onAct));
      clearTimers();
    };
  }, [user, logout, resetInactivityTimer]);

  useEffect(() => {
    const token = localStorage.getItem('elite_admin_token');
    if (!token) { setLoading(false); return; }
    const exp = decodeJwtExp(token);
    if (exp && exp < Date.now()) {
      localStorage.removeItem('elite_admin_token');
      setLoading(false);
      return;
    }
    api
      .get('/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('elite_admin_token');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('elite_admin_token', res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};
